import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "Best Sellers", to: "/shop?collection=bestsellers" },
  { label: "New Arrivals", to: "/shop?collection=newarrivals" },
  { label: "Bridal", to: "/shop?collection=bridal" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const Header = () => {
  const { cartCount, wishlist } = useShop();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      nav(`/shop?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-brand-ivory/85 backdrop-blur-xl border-b border-brand-gold/25">
      {/* Announcement strip */}
      <div className="bg-brand-maroon text-brand-ivory text-center text-[11px] tracking-[0.24em] uppercase py-2 font-medium" data-testid="header-announcement">
        Free Shipping Above ₹999 · Anti-Tarnish Guarantee · Made in India
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-4 flex items-center justify-between gap-6">
        <button className="lg:hidden text-brand-charcoal" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
          <Menu strokeWidth={1.5} size={22} />
        </button>

        <Link to="/" className="flex flex-col items-center lg:items-start" data-testid="brand-logo">
          <span className="font-serif-brand text-2xl lg:text-3xl leading-none text-brand-maroon tracking-wide">Bindni</span>
          <span className="label-tiny text-brand-gold mt-1.5">Jewels · Since 2026</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 text-sm font-medium">
          {NAV.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              className="text-brand-charcoal hover:text-brand-maroon relative group"
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-brand-gold group-hover:w-full transition-[width] duration-300" />
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-5 text-brand-charcoal">
          <button onClick={() => setSearchOpen(true)} data-testid="header-search-btn" aria-label="Search">
            <Search strokeWidth={1.5} size={20} />
          </button>
          <Link to="/wishlist" className="relative" data-testid="header-wishlist-btn" aria-label="Wishlist">
            <Heart strokeWidth={1.5} size={20} />
            {wishlist.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 text-[10px] bg-brand-gold text-brand-charcoal w-4 h-4 flex items-center justify-center rounded-full font-semibold">{wishlist.length}</span>
            )}
          </Link>
          <Link to="/cart" className="relative" data-testid="header-cart-btn" aria-label="Cart">
            <ShoppingBag strokeWidth={1.5} size={20} />
            {cartCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 text-[10px] bg-brand-maroon text-brand-ivory w-4 h-4 flex items-center justify-center rounded-full font-semibold" data-testid="cart-count">{cartCount}</span>
            )}
          </Link>
          {user ? (
            <Link to="/account" className="hidden md:flex items-center" data-testid="header-account-btn" aria-label="Account">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover border border-brand-gold" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-brand-maroon text-brand-ivory text-xs flex items-center justify-center font-semibold">{user.name?.[0]?.toUpperCase() || "B"}</div>
              )}
            </Link>
          ) : (
            <Link to="/login" className="hidden md:inline" data-testid="header-signin-btn" aria-label="Sign In">
              <User strokeWidth={1.5} size={20} />
            </Link>
          )}
          <Link to="/track-order" className="hidden md:inline text-brand-charcoal/70 hover:text-brand-maroon" data-testid="header-track-btn" aria-label="Track Order">
            <span className="text-[11px] tracking-[0.2em] uppercase font-medium">Track</span>
          </Link>
        </div>
      </div>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60] bg-brand-ivory/95 backdrop-blur-xl flex items-start pt-32 px-6" onClick={() => setSearchOpen(false)}>
          <form onSubmit={onSearch} className="max-w-2xl w-full mx-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-4 border-b-2 border-brand-maroon pb-3">
              <Search strokeWidth={1.5} size={24} className="text-brand-maroon" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search Kundan, Jhumka, Bridal…"
                className="flex-1 bg-transparent outline-none text-2xl font-serif-brand text-brand-charcoal placeholder:text-brand-muted/50"
                data-testid="search-input"
              />
              <button type="button" onClick={() => setSearchOpen(false)}><X strokeWidth={1.5} /></button>
            </div>
            <p className="label-tiny text-brand-muted mt-6">Try: "Rajputana", "Anti-Tarnish", "Under 999"</p>
          </form>
        </div>
      )}

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-brand-charcoal/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-[85%] max-w-sm bg-brand-ivory p-6 flex flex-col gap-6 overflow-y-auto">
            <div className="flex items-center justify-between">
              <span className="font-serif-brand text-2xl text-brand-maroon">Bindni</span>
              <button onClick={() => setOpen(false)} data-testid="mobile-close-btn"><X strokeWidth={1.5} /></button>
            </div>
            <div className="gold-divider" />
            <nav className="flex flex-col gap-4">
              {NAV.map((n) => (
                <Link
                  key={n.label} to={n.to} onClick={() => setOpen(false)}
                  className="text-lg font-serif-brand text-brand-charcoal hover:text-brand-maroon"
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {n.label}
                </Link>
              ))}
              <Link to="/track-order" onClick={() => setOpen(false)} className="text-lg font-serif-brand text-brand-charcoal">Track Order</Link>
              <Link to="/faq" onClick={() => setOpen(false)} className="text-lg font-serif-brand text-brand-charcoal">FAQ</Link>
              {user ? (
                <Link to="/account" onClick={() => setOpen(false)} className="text-lg font-serif-brand text-brand-maroon" data-testid="mobile-nav-account">My Account</Link>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="text-lg font-serif-brand text-brand-maroon" data-testid="mobile-nav-login">Sign In</Link>
              )}
            </nav>
          </aside>
        </div>
      )}
    </header>
  );
};

export default Header;
